import { viewPeriod } from '../campaign/view-period';
import { beginStartupWork, finishStartupWork } from '../diagnostics/startup-work';
import { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useLocation } from 'react-router';
import { api } from '../api/client';
import { useCampaigns, useDataHealth, useOverview, periodQuery,
  useCabinetsOverview,
  usePlanLimits,
} from '../api/hooks';
import { moscowDateTime } from '../layout/health-band';
import { useCabinetParams } from '../routes/params';
import { type Hold } from '../campaign/holds';
import { stateOf } from '../campaign/status';
import { economicsOf } from '../campaign/economics';
import { Controls } from '../campaign/controls';
import { ProductStrip } from '../campaign/thumb';
import { StableCampaignTable as CampaignTable, ВыборКолонок } from '../campaign/campaign-table';
import {
  readCols,
  saveCols,
  ВСЕ_КЛЮЧИ,
  забытьПорядок,
  сохранитьПорядок,
  читатьПорядок,
  type ColKey,
} from '../campaign/columns';
import { moveBefore } from '../clusters/visible';
import { SyncNow, useSyncNow } from './sync-now';
import { BulkBar } from './bulk-bar';
import { Dashboard, КнопкаСвернутьПоказатели } from './dashboard';
import { PeriodPicker } from './period-picker';
import { Выгрузка } from './vygruzka';
import {
  остатокСловами,
  подсказкаПределаСтавок,
  подсказкаПределаФото,
  Предел,
  ПределыПлашкой,
} from './predely';
import { МеткаЦели, ПлашкаЦели, useЦели } from './celi';
import { БЕЗ_ГРУППЫ, ВыборГруппы, КолонкаГрупп, useГруппы, useСвёрнутостьГрупп } from './gruppy';
import { cabinetName } from '../cabinet/cabinets-text';
import { Toast } from '../ui/toast';
import { SkeletonTable } from '../ui/skeleton';
import { lastDays, titleOf, type Period, периодСловами } from './period';
import { search } from './search';
import {
  arrange,
  counts,
  descendingByDefault,
  FILTERS,
  type FilterKey,
  type Item,
  type SortKey,
} from './campaign-view';
import type { CampaignRef } from '../api/types';
import { K } from '../kabinet/atoms';
import { Отказ, Пусто } from '../ui/nothing';
import { IconBox, IconClose, IconKey, IconRefresh } from '../ui/icons';
import { CampaignCards } from '../campaign/campaign-cards';
import { VisibleOnce } from '../ui/visible-once';

/**
 * Обзор кабинета: кампании и что с ними прямо сейчас.
 *
 * ГЛАВНОЕ НА ЭКРАНЕ — НЕ ЧИСЛА, А СОСТОЯНИЕ. Продавец открывает кабинет
 * с одним вопросом: всё ли работает. Список кампаний, где рядом с каждой
 * сказано «работает» или «стоит, потому что…», отвечает на него сразу;
 * таблица расходов на том же месте заставила бы искать ответ глазами.
 *
 * УДЕРЖАНИЯ БЕРУТСЯ ОДНИМ ЗАПРОСОМ на весь кабинет, а не по кампании.
 * Иначе список из сорока кампаний дал бы сорок запросов, и экран
 * открывался бы секундами.
 */

const money = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 });

/**
 * Деньги строки: расход и ДРР рядом.
 *
 * ПОРОЗНЬ ОНИ БЕСПОЛЕЗНЫ. «1 200 ₽ за 7 суток» не говорит ничего:
 * это может быть отличная кампания и может быть выброшенные деньги.
 * Разница видна только рядом с выручкой.
 */
function plural(n: number): string {
  const t = n % 10;
  const h = n % 100;
  if (t === 1 && h !== 11) return 'товар';
  if (t >= 2 && t <= 4 && (h < 12 || h > 14)) return 'товара';
  return 'товаров';
}

/**
 * Плохая ли новость — по словам самого сообщения.
 *
 * ЦВЕТ РЕШАЕТ, ДОЖДЁТСЯ ЛИ ПЛАШКА ЧТЕНИЯ: успех гаснет сам через
 * восемь секунд, отказ висит, пока его не закроют. Пропущенное
 * «кампания осталась работать» стоит денег, пропущенное
 * «кампания остановлена» — ничего.
 *
 * Сообщения приходят с сервера готовыми фразами, и разбирать их по словам
 * — способ небезупречный. Но ошибка здесь дешёвая: в худшем случае
 * удачное действие останется на экране дольше, чем нужно.
 */
function badNews(text: string | null): 'ok' | 'bad' {
  if (!text) return 'ok';
  return /отказал|не удалось|неизвест|ошибк|нельзя|не вышло|продолжает работать/i.test(text)
    ? 'bad'
    : 'ok';
}

/** «36 кампаний», «1 кампания», «2 кампании». */
function plural3(n: number): string {
  const t = n % 10;
  const h = n % 100;
  if (t === 1 && h !== 11) return 'кампания';
  if (t >= 2 && t <= 4 && (h < 12 || h > 14)) return 'кампании';
  return 'кампаний';
}

function Money({ campaign }: { campaign: CampaignRef }) {
  const e = economicsOf(campaign);

  return (
    <span
      className="w-56 shrink-0 text-right rk-text-sm rk-quiet"
      title={e.caveat ?? undefined}
      style={e.health === 'bad' ? { color: 'var(--bad-ink)', opacity: 1 } : undefined}
    >
      {money.format(campaign.spend)} ₽ · {e.text}
      {e.shaky && <span className="rk-faint"> ?</span>}
    </span>
  );
}

export function HomePage() {
  const startupWorkStarted = beginStartupWork();
  const params = useCabinetParams();
  const [note, setNote] = useState<string | null>(null);
  /** Узел в строке отбора, куда таблица ставит свою кнопку «Колонки». */
  /**
   * ═══════════════════════════════════════════════════════════════════
   * ВЫБОР СТОЛБЦОВ ЖИВЁТ У ЭКРАНА, А НЕ У ТАБЛИЦЫ.
   *
   * Кнопка «Колонки» стоит в ряду отбора и на трёх экранах эталона
   * (`грузится`, `отказ данных`, `нет ключа`) — там, где таблицы нет
   * вовсе. Пока состояние принадлежало таблице, кнопка исчезала
   * вместе с ней; сверка нашла это словами «кнопка-колонок: НЕТ
   * В ПРОДУКТЕ» на всех четырёх ширинах.
   *
   * Помнится по-прежнему в хранилище браузера (`readCols`/`saveCols`),
   * а не в памяти вкладки: выбор столбцов переживает перезагрузку.
   * ═══════════════════════════════════════════════════════════════════
   */
  const [колонки, setКолонки] = useState<Record<ColKey, boolean>>(() => readCols());
  const переключитьКолонку = (k: ColKey) => {
    const next = { ...колонки, [k]: !колонки[k] };
    setКолонки(next);
    saveCols(next);
  };

  /* ═══════════════════════════════════════════════════════════════════
     ПОРЯДОК СТОЛБЦОВ — РЯДОМ С НАБОРОМ, И ПО ТОЙ ЖЕ ПРИЧИНЕ.

     08.09.2026 владелец: «в общей таблице кампаний сделать колонки как
     в кластерах, с возможностью перетягивания».

     Живёт он здесь, а не в таблице: кнопка выбора стоит в ряду отбора
     и остаётся на экранах, где таблицы нет вовсе (довод целиком —
     у набора выше). Порядок и набор читают одни и те же два узла,
     и разлучать их значит завести два источника правды об одном.

     ХРАНИТСЯ В БРАУЗЕРЕ: выбор переживает перезагрузку, как и набор. */
  const [порядок, setПорядок] = useState<ColKey[]>(() => читатьПорядок());
  const переставитьКолонку = (что: ColKey, перед: ColKey) => {
    const next = moveBefore(порядок, что, перед) as ColKey[];
    setПорядок(next);
    сохранитьПорядок(localStorage, next);
  };
  /* «ВЕРНУТЬ КАК БЫЛО» — И НАБОР, И ПОРЯДОК ОДНОЙ КНОПКОЙ (как
     в кластерах). Человек, у которого таблица стала непонятной,
     не разбирается, что он испортил. */
  const вернутьСтолбцы = () => {
    setПорядок([...ВСЕ_КЛЮЧИ]);
    забытьПорядок(localStorage);
    const все = Object.fromEntries(ВСЕ_КЛЮЧИ.map((k) => [k, true])) as Record<ColKey, boolean>;
    const поУмолчанию = readCols.length === 0 ? все : readCols();
    setКолонки(поУмолчанию);
  };
  const go = useNavigate();

  /**
   * Выделенные кампании.
   *
   * ХРАНЯТСЯ ПО НОМЕРУ, А НЕ ПРИЗНАКОМ В СТРОКЕ: список перечитывается
   * каждые несколько минут, и признак, лежащий в строке, пропадал бы
   * вместе с ней — человек снимал бы галочки, которых уже нет.
   */
  const [filter, setFilter] = useState<FilterKey>('all');
  /**
   * ═══════════════════════════════════════════════════════════════════
   * ПО РАСХОДУ, СВЕРХУ ВНИЗ — ТАК ОТМЕЧЕНО В ЭТАЛОНЕ.
   *
   * У эталона в шапке таблицы стоит «За период ↓»: столбец денег,
   * по убыванию. Здесь было `'state'` по возрастанию — сортировка
   * по состоянию, — и первой строкой человеку показывалась кампания
   * на паузе с нулём расхода.
   *
   * ДЕНЬГИ ПЕРВЫМИ ОТВЕЧАЮТ НА ВОПРОС, С КОТОРЫМ СЮДА ПРИХОДЯТ:
   * «куда уходит бюджет». Состояние видно и так — полоской слева
   * и ярлыком в первой же клетке, — а расход виден только числом.
   * ═══════════════════════════════════════════════════════════════ */
  /* УМОЛЧАНИЕ — «сначала под управлением», а не расход. Довод целиком
     у `ступень` в `campaign-view.ts`: человек открывает кабинет, чтобы
     увидеть, что с тем, что он сюда завёл, а не где больше денег. */
  const [sort, setSort] = useState<SortKey>('главное');
  const [desc, setDesc] = useState(true);
  /**
   * Поиск по названию, номеру кампании и артикулу.
   *
   * ЖИВЁТ ЗДЕСЬ, А НЕ ВНУТРИ ТАБЛИЦЫ, И ЭТО ГЛАВНОЕ О НЁМ. Отсюда
   * тем же списком пользуются счётчики на кнопках отбора, «выделить все»
   * и полоса массовых действий. Спрячь поиск в таблицу — и «выделить
   * все» выделяло бы строки, которых человек не видит: три на экране,
   * тридцать шесть в действии, а узнал бы он об этом по счёту
   * за рекламу.
   */
  const location=useLocation();
  const [q, setQ] = useState(() => (new URLSearchParams(location.search).get('q') ?? '').slice(0,200));

  /**
   * ═══════════════════════════════════════════════════════════════════
   * ВЫБРАННАЯ ГРУППА — СОСТОЯНИЕ ЭКРАНА, А НЕ АДРЕС.
   *
   * Соблазн — положить её в адресную строку, как период. Но группа
   * не тот параметр, которым делятся: ссылкой посылают «посмотри вот
   * эту кампанию», а не «посмотри мою раскладку по направлениям».
   * Зато возврат из карточки кампании обратно в биддер должен
   * возвращать В ТУ ЖЕ ГРУППУ — иначе человек, разбиравший
   * «Рекомендации», после каждой кампании начинает сначала.
   *
   * Поэтому память — та же, что у свёрнутой колонки: `localStorage`
   * на кабинет. Своё у каждого кабинета: направления у них разные.
   * ═══════════════════════════════════════════════════════════════ */
  const [группа, setГруппа] = useState<string | null>(null);
  /* Свёрнута ли колонка групп. `null` — не выбирали, решает ширина;
     довод целиком — `useСвёрнутостьГрупп` в `pages/gruppy.tsx`. */
  const [свёрнута, setСвёрнута] = useСвёрнутостьГрупп();

  const [picked, setPicked] = useState<Set<string>>(new Set());
  const toggle = useCallback((id: string) =>
    setPicked((was) => {
      const next = new Set(was);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    }), []);
  const onSort = useCallback((key: SortKey) => {
    if (key === sort) setDesc(!desc);
    else { setSort(key); setDesc(descendingByDefault(key)); }
  }, [sort, desc]);
  const where = { wsId: params?.wsId ?? '', cabinetId: params?.cabinetId ?? '' };

  /**
   * ПЕРИОД ОДИН НА ПЛИТКИ И НА СПИСОК.
   *
   * Это и есть смысл всей затеи. Два отдельных выбора периода — сверху
   * свой, у списка свой — рано или поздно разъезжаются, и человек видит
   * «расход за период 240 000 ₽» над столбцом, который складывается
   * в 60 000. Объяснить это нечем, и после первого такого случая
   * не верят уже обоим числам.
   *
   * Здесь одно состояние, и оба запроса идут с одними и теми же
   * границами.
   */
  const [period, setPeriod] = useState<Period>(() => viewPeriod(new URLSearchParams(location.search), 7));

  const base = `/workspaces/${where.wsId}/cabinets/${where.cabinetId}`;
  // Та же выгрузка, что за кнопкой в шапке: у пустого списка своя
  // кнопка по эталону, а работа одна (ответ 32)
  const sync = useSyncNow(base, setNote);
  const health = useDataHealth(where.wsId, where.cabinetId);
  const loadedAt = (() => {
    const src = (health.data?.sources ?? []).find((x) => x.kind === 'campaigns');
    return src && src.ageMinutes !== null ? moscowDateTime(src.ageMinutes) : null;
  })();

  /* ═════════════════════════════════════════════════════════════════
     ПРЕДЕЛЫ ТАРИФА ДЛЯ СТРОКИ СОСТОЯНИЯ.

     Оба запроса уже идут на этом экране под теми же ключами
     (`cabinets/overview` — из шапки, `billing` — из капсулы остатка),
     поэтому здесь они не стоят ничего: react-query отдаёт готовое.

     ПОТОЛКА НЕТ — СТРОКИ НЕТ. «40 из ∞» не сообщает ничего, а место
     занимает.
     ═════════════════════════════════════════════════════════════ */
  const обзорКабинетов = useCabinetsOverview(where.wsId, { afterTable: where.cabinetId });

  /* Имя кабинета для шапки окна целей: «кабинет ООО «Медуза Сильвер»».
     Берётся из того же обзора, что и предел кабинетов, — второй счёт
     имени однажды разошёлся бы с первым. */
  const кабинетСейчас =
    (обзорКабинетов.data?.cabinets ?? []).find((x) => x.id === where.cabinetId) ?? null;
  const имяКабинета =
    кабинетСейчас === null
      ? ''
      : cabinetName(кабинетСейчас.displayName, кабинетСейчас.tradeMark, кабинетСейчас.wbOwnerId);

  /**
   * ═══════════════════════════════════════════════════════════════════
   * КЛЮЧА НЕТ — ЭКРАН ГОВОРИТ ОБ ЭТОМ, А НЕ ПОКАЗЫВАЕТ ПУСТОЙ СПИСОК.
   *
   * До 27.08 кабинет без ключа выглядел кабинетом без кампаний:
   * «Кампаний пока нет · идёт первая выгрузка, обычно меньше минуты».
   * Выгрузки не было и быть не могло — брать данные неоткуда, — и
   * человек ждал минуту, потом пять, потом шёл в поддержку.
   *
   * ТОЛЬКО `none` И ОТСУТСТВИЕ КЛЮЧА. `expired` и `rejected` — это
   * «ключ есть, но не работает»: о них говорит полоса предупреждения
   * наверху и экран подключения, а кампании при этом показываются
   * последние известные. Свести три случая в один значило бы стереть
   * с экрана всё, что уже выгружено.
   * ═══════════════════════════════════════════════════════════════════
   */
  const безКлюча =
    кабинетСейчас !== null &&
    (кабинетСейчас.token === null || кабинетСейчас.token.health === 'none');
  const тариф = usePlanLimits(where.wsId);

  /* ═══════════════════════════════════════════════════════════════════
     ПРЕДЕЛ ЦЕЛИ НУЖЕН ОТБОРУ, А НЕ ТОЛЬКО ГРАФЕ.

     Кнопка «сливают бюджет» считает тем же `goalClass`, которым таблица
     красит графу ДРР. Возьми она свой порог — и кнопка отбирала бы
     не то, что видно глазом: строка красная, а в отборе её нет.
     ═══════════════════════════════════════════════════════════════ */
  const цели = useЦели(base);
  const пределДрр = цели.data?.цели.drr ?? null;

  const комната = обзорКабинетов.data?.room ?? null;
  const местоКабинетов =
    комната === null || комната.limit === null
      ? null
      : { used: комната.used, limit: комната.limit };

  /* ИМЕНА ГРУПП — ОДНИМ ЧТЕНИЕМ НА ВЕСЬ ЭКРАН. Их спрашивают трое:
     колонка слева, кнопка выбора на узком и метка в каждой строке
     таблицы. Три отдельных чтения разошлись бы после первой же
     перекладки — метка в строке показывала бы старое имя. */
  const группы = useГруппы(base);
  const именаГрупп = useMemo(
    () => new Map((группы.data?.groups ?? []).map((г) => [г.id, г.name])),
    [группы.data],
  );

  const campaigns = useCampaigns(where, period);
  const summary = useOverview(where, period);
  const holds = useQuery({
    queryKey: ['holds', where.wsId, where.cabinetId, 'all'],
    queryFn: async () =>
      (
        await api<{ holds: Hold[] }>(
          `/workspaces/${where.wsId}/cabinets/${where.cabinetId}/holds`,
        )
      ).holds,
    enabled: Boolean(where.wsId && where.cabinetId),
  });

  /**
   * ═══════════════════════════════════════════════════════════════════
   * ЗАНЯТЫЕ МЕСТА СЧИТАЕТ СЛУЖБА. ЗДЕСЬ ИХ БЫЛО НЕ ИЗ ЧЕГО СОСЧИТАТЬ.
   *
   * Стояло `(campaigns.data ?? []).length` — длина списка на экране.
   * Мимо дважды.
   *
   * ▸ Потолок зовётся `managed_campaigns` и считается по ВКЛЮЧЁННЫМ
   *   биддерам (`SELECT count(*) FROM bidders WHERE enabled`,
   *   `src/billing/usage.service.ts`), а не по всем кампаниям
   *   Wildberries. У продавца с сорока кампаниями и тремя под
   *   управлением плашка писала «40 из 50» вместо «3 из 50»,
   *   а при шестидесяти — «60 из 50»: «вы за потолком» тому, кто
   *   до потолка не дошёл.
   *
   * ▸ Потолок один на ПРОСТРАНСТВО, а список на экране — один кабинет
   *   из пяти. Даже посчитав включённые биддеры верно, экран назвал бы
   *   пятую часть числа.
   *
   * Теперь оно приходит из `GET /workspaces/:id/billing` вместе
   * с потолком — той же записью, по которой служба считает заморозку.
   *
   * Ни одна проверка этого не видела: у заглушки пробы стоял потолок
   * 100 при восьми кампаниях, и «8 из 100» совпадало по ширине
   * с эталонными «40 из 50» — четыре цифры против четырёх. Нашлось
   * на экране «нет ключа», где эталон пишет «0 из 50».
   * ═══════════════════════════════════════════════════════════════════
   */
  const местоКампаний =
    тариф === null || тариф.campaigns === null || тариф.занятоКампаний === null
      ? null
      : { used: тариф.занятоКампаний, limit: тариф.campaigns };

  /* Пределы опытов приходят тем же запросом, что и потолки тарифа.
     `предел === null` означает «тариф про это молчит»: пары нет. */
  const опытыСтавок =
    тариф?.тесты?.ставки?.предел === null || тариф?.тесты?.ставки === undefined
      ? null
      : { занято: тариф.тесты.ставки.занято, предел: тариф.тесты.ставки.предел };
  const опытыФото =
    тариф?.тесты?.фото?.предел === null || тариф?.тесты?.фото === undefined
      ? null
      : { занято: тариф.тесты.фото.занято, предел: тариф.тесты.фото.предел };

  /**
   * Состояние каждой кампании — из трёх источников сразу: наше удержание,
   * статус у WB и расход. Первая редакция экрана брала только удержания
   * и потому уверенно писала «все работают» под четырьмястами
   * завершёнными кампаниями.
   */
  const all = useMemo(() => {
    const hs = holds.data ?? [];
    // Порядок здесь больше не задаётся: его выбирает человек, и жёсткая
    // сортировка тут спорила бы с выбранной — тихо, через раз.
    return (campaigns.data ?? []).map((c) => ({ campaign: c, state: stateOf(c, hs) }));
  }, [campaigns.data, holds.data]);

  const byId = useMemo(() => new Map(all.map((r) => [r.campaign.id, r])), [all]);

  // Завершённых и отказов сервер не отдаёт вовсе: вернуть их в работу
  // нельзя, и место в списке они занимали зря.
  /**
   * ОТБОР И ПОРЯДОК РАБОТАЮТ ВМЕСТЕ С ВЫДЕЛЕНИЕМ. «Выделить все»
   * выделяет ПОКАЗАННЫЕ строки, а не все строки кабинета: человек видит
   * три, а действие ушло бы на тридцать шесть — и узнал бы он об этом
   * по счёту за рекламу.
   */
  // ПОИСК ИДЁТ ПЕРВЫМ, ДО ОТБОРА И СЧЁТЧИКОВ. Тогда «работают 2»
  // на кнопке означает «из найденного работают 2» — то, что человек
  // и видит под ней.
  /* ГРУППА ОТБИРАЕТ ДО ПОИСКА И ДО СЧЁТЧИКОВ. Тогда «работают 2»
     на кнопке означает «из этой группы работают 2» — ровно то, что
     человек видит под ней. Отбери группу после — и счётчики считали
     бы весь кабинет, а таблица показывала бы горстку. */
  const вГруппе = useMemo(() =>
    группа === null
      ? all
      : группа === БЕЗ_ГРУППЫ
        ? all.filter((r) => r.campaign.groupId === null)
        : all.filter((r) => r.campaign.groupId === группа), [all, группа]);

  const items: Item[] = useMemo(() => search(
    вГруппе.map(({ campaign: c, state }) => ({ ...c, state })),
    q,
  ), [вГруппе, q]);
  const shown = useMemo(() => arrange(items, filter, sort, desc, пределДрр), [items, filter, sort, desc, пределДрр]);
  const found = useMemo(() => counts(items, пределДрр), [items, пределДрр]);
  // Складываем ПОКАЗАННОЕ: суммы должны сходиться с тем, что под ними.
  /**
   * ═══════════════════════════════════════════════════════════════════
   * ИТОГА ПОД ТАБЛИЦЕЙ НЕТ ВОВСЕ — 14.09.2026, РЕШЕНИЕ ВЛАДЕЛЬЦА.
   *
   * Сперва отсюда убрали общий ДРР: он считался по заказам, а строки
   * над ним — по выкупам, и число выходило вдвое ниже слагаемых.
   * Остались расход и заказы, и владелец посмотрел ещё раз: «итого
   * за период и заказов не убрано».
   *
   * ОН ПРАВ, И ВОТ ЧЕМ. Те же две суммы уже стоят В ПЛИТКАХ НАД
   * ТАБЛИЦЕЙ — «Расход за период» и «Заказы с рекламы», — только
   * посчитанные сервером и по всему кабинету. Строка внизу повторяла
   * их по показанным строкам, то есть иначе, и совпадала с ними лишь
   * пока не нажат ни один отбор. Два числа с одной подписью,
   * расходящиеся от нажатия кнопки, — это не итог, а ловушка.
   * ═══════════════════════════════════════════════════════════════════
   */
  /**
   * ЧЕМ КРАСИТСЯ ЦЕЛЬ, КОГДА СВОЕГО ЧИСЛА БОЛЬШЕ НЕТ.
   *
   * Тем же, чем плитка: `drrTotal` — весь расход, делённый на заказы
   * кабинета, приведённые долей выкупа. Не «похожим числом», а ТЕМ ЖЕ:
   * метка и плитка обязаны выносить один приговор, иначе на экране
   * снова окажутся два разных ответа на один вопрос.
   *
   * Разойтись они не могут и по счёту: `goalClass` в кабинете
   * и `drrLevel` в службе считают по одной грани, и за тем, чтобы
   * ГРАНЬ у обоих совпадала, стоит `celi-gran.spec.ts`.
   */
  const дррКабинета = summary.data?.totals.drrTotal ?? null;
  /**
   * ═══════════════════════════════════════════════════════════════════
   * ОБЩЕГО ДРР ЗДЕСЬ НЕТ, И ЭТО РЕШЕНИЕ, А НЕ ПРОПУСК.
   *
   * Здесь стояла дробь `весь расход ÷ вся выручка`, где выручка бралась
   * ПО ЗАКАЗАМ (`i.revenue`). А графа ДРР в каждой строке считается
   * сервером ПО ВЫКУПАМ: `расход ÷ (выручка × доля выкупа товара)`.
   * Между основаниями стоит доля выкупа, то есть примерно двойка,
   * и итог выходил вдвое ниже строк, которые он подводит.
   *
   * 14.09.2026 на экране это выглядело так: строки 126 %, 80,5 %,
   * 63,4 %, 60,7 % — и под ними «ДРР 12,7 % · цель 14 %» зелёной
   * меткой. Четыре кампании, сжигающие деньги, стояли над строкой,
   * которая говорила, что всё в порядке.
   *
   * ПОЧЕМУ УБРАЛИ, А НЕ ПОЧИНИЛИ. Починить можно было двумя способами,
   * и оба честные: общая дробь по выкупам дала бы 28,4 %, среднее
   * из строк — 54,0 %. Владелец посмотрел на оба и сказал: «эти данные
   * нужно вообще убрать, а то они вводят в заблуждение. Пусть
   * отображается ДРР в самих кампаниях, а тут вообще не нужен».
   *
   * И он прав по существу. Над этой строкой уже висит плитка «ДРР
   * по выкупам» — там знаменатель ТРЕТИЙ, заказы всего кабинета,
   * включая органику. Три числа с одним названием на одном экране
   * не различит никто: каждое отвечает на свой вопрос, а выглядят они
   * как одно, посчитанное трижды с ошибками.
   *
   * Осталось два, и у каждого своё место: плитка отвечает «сколько
   * бизнес отдаёт рекламе», строка — «окупается ли эта кампания».
   * Складывать строки в одно число незачем: их и читают по одной,
   * а сравнивать даёт сортировка по графе.
   * ═══════════════════════════════════════════════════════════════════
   */
  // Preserve table props when only summary, billing or service data arrive.
  const rows = useMemo(() => shown.map((i) => ({ campaign: byId.get(i.id)!.campaign, state: i.state })), [shown, byId]);

  if (!params) return null;

  /**
   * ═══════════════════════════════════════════════════════════════════
   * ЧТО СТОИТ НА МЕСТЕ ТАБЛИЦЫ — ОДНИМ РАЗБОРОМ, А НЕ ПЯТЬЮ `&&`
   * ПОДРЯД В РАЗМЕТКЕ.
   *
   * Пять условий, стоящих порознь, однажды совпадут: пустой список
   * при упавшей выгрузке рисовал бы и «кампаний нет», и «не
   * прочиталось» — два ответа на один вопрос, противоречащих друг
   * другу. Здесь состояние ровно одно, и порядок разбора — ОТ БЕДЫ
   * К НОРМЕ: сперва то, что человеку надо чинить.
   *
   * ЭТО ЖЕ ИМЕНА ЭКРАНОВ ЭТАЛОНА (`нет ключа`, `отказ данных`,
   * `грузится`, `группа пуста`, `ничего не нашлось`) — сверка ходит
   * по ним же.
   *
   * `пусто` — наш случай, эталоном не нарисованный: ключ работает,
   * первая выгрузка ещё идёт. Узла с именем у него нет, и сверке
   * он не виден.
   * ═══════════════════════════════════════════════════════════════════
   */
  const состояние: 'нет-ключа' | 'отказ' | 'грузится' | 'пусто' | 'не-нашлось' | 'таблица' =
    безКлюча
      ? 'нет-ключа'
      : campaigns.isError
        ? 'отказ'
        : campaigns.isLoading
          ? 'грузится'
          : all.length === 0
            ? 'пусто'
            : вГруппе.length > 0 && rows.length === 0 && q !== ''
              ? 'не-нашлось'
              : 'таблица';

  /**
   * ГРУППА ПУСТА — НЕ ВМЕСТО ТАБЛИЦЫ, А ПОД НЕЙ. Так в эталоне:
   * шапка таблицы остаётся на месте (столбцы видно, порядок понятен),
   * а блок стоит ниже. Пустая таблица без шапки читалась бы как
   * «таблицы нет вовсе».
   */
  const группаПуста = состояние === 'таблица' && группа !== null && вГруппе.length === 0;

  return finishStartupWork('home.render', startupWorkStarted, (
    /* Поля и зазор — от коробки экрана (`layout/screen.ts`). */
    <div className="flex min-w-0 flex-col" style={{ gap: 'inherit' }}>
      {/* ЗАГОЛОВОК НАЗЫВАЕТ ПЕРИОД, А ПОДПИСЬ ПОД НИМ — СВЕЖЕСТЬ ДАННЫХ.
          Два вопроса, которые задают, глядя на числа: «за что это
          посчитано» и «на какой момент». Первый ответ в заголовке, потому
          что читают его первым; второй мельче и ниже.

          ОРГАНЫ УПРАВЛЕНИЯ СПРАВА, в одной строке с заголовком. Оба —
          и период, и обновление — отвечают на те же два вопроса, и стоять
          им нужно вместе. Отдельная строка ради одной кнопки съедала бы
          высоту экрана. */}
      {/* ═══════════════════════════════════════════════════════════════
          ПЛАШКА «ПОСТАВЬТЕ ПРЕДЕЛ ДРР» — ВЫШЕ ЗАГОЛОВКА.

          Так в эталоне (y = 109 при заголовке на 223), и по делу: пока
          предела нет, каждая строка таблицы отвечает «данных мало»,
          и объяснить это надо ДО того, как человек начнёт читать
          пустые вердикты. Довод целиком — у самой плашки.
          ═══════════════════════════════════════════════════════════ */}
      <ПлашкаЦели base={base} кабинет={имяКабинета} />

      <div className={K.шапкаЭкрана}>
        <div className={K.столбикЗаголовка}>
          {/* ЗАГОЛОВОК НАЗЫВАЕТ ПЕРИОД — «Кампании за последние 7 дней».
              Так в эталоне, и потому же на самой кнопке периода
              осталась только пара дат: название стоит здесь, крупно,
              и повторять его на кнопке незачем. */}
          <h1 className={K.заголовокЭкрана}>Кампании {периодСловами(period)}</h1>
          {/* КОГДА ВЫГРУЖЕНО — РЯДОМ С ЧИСЛОМ КАМПАНИЙ, как в эталоне.
              Число без времени отвечает на «сколько», но не на «за какой
              день»: выгрузка идёт по расписанию, и разница между «час
              назад» и «вчера» здесь важнее самого числа.

              ВРЕМЯ СЧИТАЕТСЯ ИЗ ВОЗРАСТА ВЫГРУЗКИ, который отдаёт
              `data-health`, — тем же способом, что и полоса достоверности
              над экраном. Своего источника времени тут нет намеренно:
              два места, считающие одно и то же по-разному, однажды
              разойдутся на глазах у человека. */}
          <span className={K.подписьЭкрана}>
            {loadedAt && `Выгружено ${loadedAt} · `}
            {all.length > 0 ? `${all.length} ${plural3(all.length)}` : 'кабинет пуст'}
          </span>
        </div>

        {/* РЯД ДЕЙСТВИЙ — ЧАСТИЦЕЙ ЭТАЛОНА, А НЕ `ml-auto flex gap-2`.
            Разница не косметическая: в частице стоит `flex: 0 0 auto`,
            и без него кнопка периода растягивалась на 371 пиксель
            против эталонных 235 — на снимке это первое, что видно. */}
        <div className={K.рядДействийЭкрана}>
          {/* СВЁРТКА ПОКАЗАТЕЛЕЙ — В РЯДУ ПЕРИОДА, ПЕРЕД ВЫГРУЗКОЙ
              (передача 28.08, раздел 2). Место выбрано не случайно:
              человек, которому нужна таблица целиком, приходит сюда же
              за периодом, и обе ручки о плотности экрана стоят рядом. */}
          <PeriodPicker value={period} onChange={setPeriod} />
          {/* ВЫГРУЗКА СТОИТ РЯДОМ С ПЕРИОДОМ, А НЕ ПОД ТАБЛИЦЕЙ: выгружают
              ровно то, что отобрали, и решение принимается в ту же
              секунду, что и «отобрать» (то же соображение, что в журнале) */}
          {/* КНОПКА СТОИТ И БЕЗ КЛЮЧА — так в эталоне. Плиток там нет,
              и свёртывать нечего; но кнопка — про ПЛОТНОСТЬ ЭКРАНА,
              а не про числа, и её исчезновение меняло ширину всего ряда
              (сверка на «нет ключа @ 390»: кнопка периода 255 против
              эталонных 207). Выбор человека при этом сохраняется
              и сработает, как только ключ появится. */}
          <КнопкаСвернутьПоказатели />
          <Выгрузка base={base} q={periodQuery(period)} />
          <SyncNow base={base} onDone={setNote} />
        </div>
      </div>

      {/* ПЛИТКИ УХОДЯТ ВМЕСТЕ С КЛЮЧОМ. Восемь показателей, посчитанных
          из ничего, — это восемь прочерков поперёк экрана; эталон
          на `нет ключа` не рисует их вовсе, и это верно: считать нечего,
          и говорить об этом восемь раз незачем. */}
      {!безКлюча && (
        <Dashboard
          tiles={summary.data?.tiles}
          loading={summary.isLoading}
          error={Boolean(summary.error)}
        />
      )}

      {/* ОТВЕТ ПРИКЛЕЕН К ОКНУ, А НЕ К НАЧАЛУ СТРАНИЦЫ.
          Здесь он и стоял — и был честен ровно до того дня, когда над
          списком встали восемь плиток и строка отбора. Человек, нажавший
          тумблер на двадцатой строке, ответа не видел вовсе: тот уходил
          на два экрана вверх, и нажатие читалось как «ничего
          не происходит». */}
      <Toast text={note} kind={badNews(note)} onClose={() => setNote(null)} />

      {/* ОТБОР, ИТОГ И ТАБЛИЦА — ОДНОЙ КАРТОЧКОЙ, как в эталоне.
          Прежде это были три отдельных блока на фоне страницы: строка
          фильтров висела сама по себе, итог — сам по себе, и таблица
          прокручивала всю страницу вместе с шапкой и плитками.
          В эталоне карточка одна, а прокручивается только таблица
          внутри неё — шапка кабинета и плитки остаются на месте. */}
      {/* ═══════════════════════════════════════════════════════════════
          КОЛОНКА ГРУПП — СЛЕВА ОТ ТАБЛИЦЫ, В ОДНОМ РЯДУ С НЕЙ.

          Так в эталоне (`ряд-с-группами`). Не над таблицей и не в ряду
          отборов: групп бывает двадцать, и полоса из двадцати чипов
          заняла бы три строки над каждым экраном. Колонка стоит
          на месте, прокручивается своей полосой и сворачивается —
          тому, кто группами не пользуется, она не мешает.

          НА УЗКОМ ЭКРАНЕ КОЛОНКИ НЕТ ВОВСЕ: 216 пикселей — больше
          половины телефона. Там её заменяет кнопка выбора группы
          в ряду отборов, и так же в эталоне.
          ═══════════════════════════════════════════════════════════ */}
      {/* ПЛАШКА ПРЕДЕЛОВ НА ТЕЛЕФОНЕ — НАД ТАБЛИЦЕЙ, отдельным блоком.
          Так в эталоне: на 390 полосы итогов нет, и пределы вынесены
          сюда. Довод целиком — `pages/predely.tsx`. */}
      {/* ═══════════════════════════════════════════════════════════════
          КАРТОЧКА СТОИТ ВСЕГДА, А НЕ ТОЛЬКО КОГДА КАМПАНИИ ЕСТЬ.

          Здесь было `all.length > 0` на плашке пределов, на ряде
          с группами и на всём, что внутри, — и на четырёх экранах
          эталона из этого выходил голый фон: `грузится`, `отказ
          данных`, `нет ключа`, `группа пуста` рисуют И строку отбора,
          И колонку групп, И плашку пределов. Пропадала не «пустая
          рамка», а поиск, отбор, выбор группы и счёт мест в тарифе —
          ровно то, чем человек пользуется, когда список пуст.

          Меняется ТОЛЬКО то, что стоит на месте таблицы (`состояние`).
          ═══════════════════════════════════════════════════════════ */}
      <span className="rk-only-narrow">
        <ПределыПлашкой
          кабинеты={местоКабинетов}
          кампании={местоКампаний}
          тестыСтавок={опытыСтавок}
          тестыФото={опытыФото}
        />
      </span>

      {
        <div data-d="ряд-с-группами" className={K.рядСГруппами}>
        {/* ═══════════════════════════════════════════════════════════
            КОЛОНКА ПРЯЧЕТСЯ ПРАВИЛОМ, А НЕ ЗАМЕРОМ ШИРИНЫ В КОДЕ:
            замер в браузере выдаёт первое значение уже после
            отрисовки, и на телефоне колонка успевает мигнуть.
            Границу держит сторож `ui/breakpoints.spec.ts`.

            Класс идёт ВНУТРЬ, на сам `aside`: обёртка с
            `display: contents` перебивала бы `display: none`
            правила своим встроенным стилем — и колонка оставалась
            бы на 390. Поймано сверкой: «колонка-групп: есть
            в продукте, нет в эталоне».

            ГРАНИЦА У КОЛОНКИ СВОЯ — 1360, А НЕ 1024 (`rk-rail-fits`).
            Так в эталоне: `railFits = !phone && w >= 1360`. Ниже
            колонки нет вовсе, и группу выбирают кнопкой в ряду
            отборов. Пока стояло `rk-only-wide`, весь пролёт
            1024…1359 показывал колонку там, где эталон её убирает,
            — и сверка этого не видела, потому что мерила 1440 и 390.

            СВЁРНУЛИ РУКАМИ — КОЛОНКИ НЕТ СОВСЕМ, а не полоска
            с кареткой: `railOpen: railOn` в эталоне выключает весь
            `aside`. Вернуть её можно из того же выпадения, пунктом
            «Показать колонкой».
            ═══════════════════════════════════════════════════════ */}
        {свёрнута !== true && (
          <КолонкаГрупп
            base={base}
            className={свёрнута === false ? 'rk-only-wide' : 'rk-rail-fits'}
            выбрана={группа}
            свернуть={() => setСвёрнута(true)}
            выбрать={(id) => {
              setГруппа(id);
              /* ВЫДЕЛЕНИЕ СБРАСЫВАЕТСЯ ПРИ СМЕНЕ ГРУППЫ — по той же
                 причине, что и при смене отбора: иначе в нём остаются
                 строки, которых человек больше не видит, и общее
                 действие уходит на них. */
              setPicked(new Set());
            }}
          />
        )}
        <div style={{ flex: '1 1 auto', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
        <div className="rk-card" style={{ overflow: 'hidden' }}>
        <div className={K.рядОтбора}>
          {/* ПОИСК ПЕРВЫМ, ЛЕВЕЕ ОТБОРА. Отбор отвечает на вопрос
              «какие кампании такие-то», поиск — на другой и куда более
              частый: «где вот эта». Человек приходит с готовым ответом
              в голове — с артикулом из отчёта, с номером из письма
              Wildberries, с обрывком названия — и ему нужно не сравнить
              тридцать шесть строк, а найти одну. */}
          {/* ОБЁРТКА ПОЛЯ — ЧАСТИЦЕЙ: в ней `flex: 1 1 220px` и
              `min-width: 180px`, то есть поле тянется, пока хватает
              места, и первым уходит на новую строку. С голым
              `relative` оно выходило 213 пикселей против эталонных 266
              и не тянулось вовсе. */}
          <span className={K.уПоляПоиска}>
            <span aria-hidden="true" className={K.значокПоиска}>
              <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                <circle cx="7" cy="7" r="4.6" />
                <path d="m10.4 10.4 3.1 3.1" strokeLinecap="round" />
              </svg>
            </span>
            <input
              className={K.полеПоиска}
              data-d="поле-поиска"
              style={{ paddingRight: q ? 28 : undefined }}
              value={q}
              placeholder="Поиск: название, номер, артикул"
              aria-label="Поиск по кампаниям"
              onChange={(e) => {
                setQ(e.target.value);
                // ВЫДЕЛЕНИЕ СБРАСЫВАЕТСЯ, как и при смене отбора: иначе
                // в нём остаются строки, которых человек больше не видит,
                // — и действие уходит на них.
                setPicked(new Set());
              }}
            />
            {q && (
              <button
                className="rk-icon-button absolute top-1/2 right-2 -translate-y-1/2"
                aria-label="Очистить поиск"
                title="Очистить"
                onClick={() => {
                  setQ('');
                  setPicked(new Set());
                }}
              >
                ✕
              </button>
            )}
          </span>

          {/* ═══════════════════════════════════════════════════════
              ИМЯ СТОИТ НА ПОЛОСЕ ОТБОРА, А НЕ НА КНОПКЕ В НЕЙ.

              Первая разметка повесила `отбор-состояний` на каждый чип,
              и сверка сравнила полосу эталона шириной 890 с чипом
              шириной 62 — семнадцать «расхождений» на ровном месте.
              В эталоне это прокручиваемая полоса целиком: у неё свой
              перенос и свой зазор, а у чипа — свои.

              СЧЁТЧИК НА КНОПКЕ СЧИТАЕТСЯ ТЕМ ЖЕ ПРАВИЛОМ, что и сам
              отбор: иначе на кнопке «не тратят 3», а после нажатия
              строк две.
              ═══════════════════════════════════════════════════ */}
          <span data-d="отбор-состояний" data-scroll="1" className={K.отборСостояний}>
          {FILTERS.map((f) => (
            <button
              key={f.key}
              title={f.hint}
              onClick={() => {
                setFilter(f.key);
                // ВЫДЕЛЕНИЕ СБРАСЫВАЕТСЯ ПРИ СМЕНЕ ОТБОРА. Иначе
                // в нём остаются строки, которых человек больше
                // не видит, — и действие уходит на них.
                setPicked(new Set());
              }}
              /* ДВЕ ЧАСТИЦЫ, А НЕ ОДНА С ДОБАВКОЙ. Выбранный чип
                 в эталоне — отдельный вид целиком (чёрная заливка,
                 белые буквы, полужирный), и снимается он с первой
                 кнопки полосы, покой — со второй. Пока частица была
                 одна и резалась с первой, выбранный вид доставался
                 ВСЕМ семи кнопкам: полоса выходила сплошь чёрной.

                 ПУСТОЙ ОТБОР ГАСНЕТ (`op: .5` в эталоне) — кроме
                 «все»: он не бывает пустым, у него в счёте всё. */
              className={
                (f.key === filter ? K.чипОтбораВыбран : K.чипОтбора) +
                (found[f.key] === 0 && f.key !== 'all' ? ' rk-filter-empty' : '')
              }
            >
              {/* ПРОБЕЛ МЕЖДУ СЛОВОМ И СЧЁТОМ — КАК В ЭТАЛОНЕ
                  (`<span>все</span> <span>36</span>`). На ширину он
                  не влияет: у кнопки `display:inline-flex`, а пробел
                  между гибкими детьми выкладка отбрасывает. Он нужен
                  озвучивателю и переносу в буфер обмена: без него
                  строка кнопки читается «все36». */}
              {f.title}{' '}
              <span style={{ opacity: 0.65 }}>{found[f.key]}</span>
            </button>
          ))}
          </span>

          {/* ═══════════════════════════════════════════════════════
              ВЫБОР ГРУППЫ — ПОСЛЕ ПОЛОСЫ ОТБОРОВ, А НЕ ПЕРЕД НЕЙ.

              Так в эталоне на 390: поле поиска, полоса отборов,
              выбор группы, «Колонки». Стоял он вторым — и занимал
              собой всю вторую строку ряда, отодвигая отборы на третью:
              между полем поиска и кнопками отбора вставала полоса,
              которая к ним не относится.

              ТАМ, ГДЕ КОЛОНКИ НЕТ, — И ТОЛЬКО ТАМ. Два органа на одно
              и то же значили бы, что человек жмёт то кнопку, то строку
              в колонке и не понимает, чем они различаются; эталон их
              и разводит: `railClosed: !railOn`.

              А «где колонки нет» — это три разных случая, и потому
              класс считается, а не написан:
                · не выбирали руками  → ниже 1360 (`rk-rail-tight`);
                · свернули руками     → везде, класса нет вовсе;
                · развернули руками   → только телефон (`rk-only-narrow`).

              «Показать колонкой» отдаётся только с широкого экрана:
              на телефоне колонки не бывает (`railCanOpen: !phone`),
              и пункт, который ничего не сделает, там не нужен. */}
          <ВыборГруппы
            base={base}
            className={
              свёрнута === true ? undefined : свёрнута === false ? 'rk-only-narrow' : 'rk-rail-tight'
            }
            выбрана={группа}
            развернуть={свёрнута === false ? undefined : () => setСвёрнута(false)}
            выбрать={(id) => {
              setГруппа(id);
              setPicked(new Set());
            }}
          />

          {/* ПОРЯДОК И НАПРАВЛЕНИЕ — ОДИН ЭЛЕМЕНТ, А НЕ ДВА В РАЗНЫХ
              КОНЦАХ СТРОКИ. Стрелка стояла последней, за кнопкой
              «Колонки», и при переносе строки уезжала на вторую —
              отдельно от списка, которым она управляет. Прочитать её
              там было невозможно: стрелка вверх сама по себе не значит
              ничего. */}
          {/* ПОРЯДОК И «КОЛОНКИ» — ОДНОЙ ГРУППОЙ У ПРАВОГО КРАЯ.
              Порознь они разъезжались: строка отбора переносится,
              и «Колонки» уезжали на вторую строку к ЛЕВОМУ краю —
              а список столбцов, который висит правым краем на кнопке,
              вылезал за границу окна и терял левую половину.
              Обе управляют видом таблицы, а не отбором, и стоять
              им вместе. */}
          {/* ═══════════════════════════════════════════════════════
              «ПОРЯДОК» ВЫПАДЕНИЕМ УБРАН — ЕГО НЕТ В ЭТАЛОНЕ.

              Здесь стоял список из восьми полей и стрелка направления.
              В эталоне порядок задаётся ИНАЧЕ: нажатием на заголовок
              столбца, и стрелка стоит в самом заголовке («За период ↓»).
              Так понятнее — человек сортирует то, на что смотрит, —
              и не отнимает у строки отбора двести пикселей.

              Сама сортировка никуда не делась: `onSort` у таблицы
              работает, заголовки нажимаются, направление переворачивается
              повторным нажатием. Убрано второе управление тем же.
              ═══════════════════════════════════════════════════════ */}
          {/* БЕЗ `ml-auto`: в эталоне «Колонки» — просто следующий
              элемент строки отбора и при переносе встаёт СЛЕВА
              на второй строке, под поиском. Прижатая вправо кнопка
              висела в одиночестве у правого края — и читалась как
              часть таблицы, а не как часть отбора. */}
          <span className="flex flex-wrap items-center gap-3">
          {/* КНОПКА «КОЛОНКИ» — в строке отбора, как в эталоне, и стоит
              она здесь ВСЕГДА: на трёх экранах эталона таблицы нет,
              а ряд отбора есть весь. Довод целиком — `ВыборКолонок`. */}
          <ВыборКолонок
            on={колонки}
            flip={переключитьКолонку}
            порядок={порядок}
            переставить={переставитьКолонку}
            вернутьКакБыло={вернутьСтолбцы}
          />
          </span>
        </div>

      {/* ═══════════════════════════════════════════════════════════════
          НА МЕСТЕ ТАБЛИЦЫ — РОВНО ОДНО ИЗ ПЯТИ. Порядок и слова
          по эталону; какое именно, решено выше одним разбором.
          ═══════════════════════════════════════════════════════════ */}

      {/* КЛЮЧА НЕТ — И ЭТО НЕ «КАМПАНИЙ НЕТ». Единственный экран
          кабинета, где вместо восьми плиток и таблицы стоит одна
          кнопка: пока ключа нет, брать данные неоткуда, и всё
          остальное на экране было бы прочерками. */}
      {состояние === 'нет-ключа' && (
        <Пусто
          вид="ключ"
          имя="состояние-нет-ключа"
          знак={<IconKey size={30} штрих={1.2} />}
          что="Кабинет Wildberries не подключён"
          почему="Пока нет ключа доступа, сервису нечего показывать: кампании, расход и заказы приходят из вашего кабинета продавца. Ставки и настройки при подключении не меняются."
          кнопка={{
            текст: 'Подключить кабинет',
            главная: true,
            имя: 'кнопка-подключить-кабинет',
            жать: () => go(`/rk/${params.wsId}/connect`),
          }}
          подпись="нужен ключ из Wildberries → Профиль → Настройки → Доступ к API"
        />
      )}

      {/* ВЫГРУЗКА УПАЛА — ГОВОРИМ «НЕ ПРОЧИТАЛОСЬ», А НЕ «НЕТ КАМПАНИЙ».
          Человеку, у которого упала выгрузка, «кампаний нет» — это
          ложь, от которой он пойдёт искать пропавшие кампании
          в Wildberries. Слова эталона, и главный вопрос («деньги
          и настройки целы?») в них отвечен прямо. */}
      {состояние === 'отказ' && (
        <Отказ
          имя="состояние-отказ"
          что="Кампании не прочитались"
          почему={
            'Wildberries не ответил на запрос списка. Это про сеть, а не про ваши кампании: ' +
            'деньги на счетах и настройки не тронуты.' +
            (loadedAt === null ? '' : ` Плитки выше посчитаны по последней удачной выгрузке — ${loadedAt}.`)
          }
          кнопка={{ текст: 'Повторить', жать: () => void campaigns.refetch() }}
        />
      )}

      {/* СКЕЛЕТ ВМЕСТО СЛОВА «ЧИТАЮ». Слово занимает строку, таблица —
          весь экран: когда данные приходят, страница прыгает, и рука,
          занесённая над кнопкой, попадает не туда. Скелет держит место
          и формой говорит, ЧТО грузится. */}
      {состояние === 'грузится' && <SkeletonTable имя="состояние-грузится" />}

      {/* ═══════════════════════════════════════════════════════════════
          ПУСТОЙ СПИСОК ГОВОРИТ, ЧТО МЫ ДЕЛАЕМ, А НЕ ЧТО НЕ ДЕЛАЕМ.

          Прежняя надпись объясняла, почему кампаний нет, — «выгрузка
          идёт по расписанию». Человек, который минуту назад отдал ключ
          от своего кабинета Wildberries, читает это как «не работает»
          и уходит. На деле выгрузка запускается прямо при подключении,
          а список сам переспрашивает себя, пока пуст: ждать надо
          не расписания, а минуту.

          ЭТОГО ЭКРАНА У ЭТАЛОНА НЕТ — у него ключ либо есть, либо нет.
          Наш случай третий: ключ работает, а первая выгрузка ещё идёт.
          ═══════════════════════════════════════════════════════════ */}
      {состояние === 'пусто' && (
        <Пусто
          знак={<IconRefresh size={30} штрих={1.2} />}
          что="Кампаний пока нет"
          почему={
            'Если они есть в Wildberries — идёт первая выгрузка, обычно меньше минуты. ' +
            'Список обновится сам; можно и поторопить.'
          }
          кнопка={{ текст: 'Синхронизировать', жать: () => void sync.mutate() }}
        />
      )}

      {/* НАШЛОСЬ НОЛЬ — ГОВОРИМ ОБ ЭТОМ ПРЯМО, а не показываем пустое
          место. Пустая таблица без объяснения читается как поломка
          или как «кампаний нет вовсе»; ни то, ни другое не верно. */}
      {состояние === 'не-нашлось' && (
        <Пусто
          имя="состояние-ничего-не-нашлось"
          что={`По запросу «${q}» ничего не нашлось`}
          почему="Поиск смотрит на название, номер кампании и артикулы её товаров. Проверьте раскладку и попробуйте часть слова."
          кнопка={{ текст: 'Показать все', жать: () => setQ('') }}
        />
      )}

      {состояние === 'таблица' && (
        <>

        {/* ПОЛОСА ВЫДЕЛЕНИЯ — НА УТОПЛЕННОМ ФОНЕ, между отбором
            и таблицей: она про то, что сейчас показано и сколько это
            денег, и стоять ей ровно здесь. */}
        <div className={K.полосаИтогов} data-d="полоса-итогов">
          {/* ВЫДЕЛИТЬ ВСЕ — ГАЛОЧКОЙ, А НЕ ПОДЧЁРКНУТЫМ СЛОВОМ.
              Подчёркнутое слово читается как ссылка «куда-то», а действие
              здесь то же самое, что у галочек в строках, — и выглядеть
              оно должно так же. */}
          <label className="flex cursor-pointer items-center gap-2">
            <input
              type="checkbox"
              checked={picked.size > 0 && picked.size === rows.length}
              // «Часть выделена» — не то же самое, что «ничего не выделено»:
              // без этого признака галочка врёт при выборе двух строк из сорока.
              ref={(el) => {
                if (el) el.indeterminate = picked.size > 0 && picked.size < rows.length;
              }}
              onChange={() =>
                setPicked(
                  picked.size === rows.length ? new Set() : new Set(rows.map((r) => r.campaign.id)),
                )
              }
            />
            {/* СЛОВА ЭТАЛОНА: подпись у галочки не меняется, а сколько
                выделено — сказано отдельной строкой рядом. Прежде
                подпись превращалась в «выделено 3», и галочка,
                у которой меняется имя, перестаёт быть галочкой
                «выделить все». */}
            <span>Выделить все</span>
          </label>
          <span style={{ color: 'var(--ink-3)', whiteSpace: 'nowrap' }}>
            {picked.size > 0 ? `выделено ${picked.size}` : 'ничего не выделено'}
          </span>
          {(filter !== 'all' || q !== '' || группа !== null) && (
            <span style={{ color: 'var(--ink-3)' }}>показано {rows.length}</span>
          )}

          {/* ═══════════════════════════════════════════════════════════
              ЧИП ВЫБРАННОЙ ГРУППЫ — «группа «Лето»» с крестиком.

              Выбранная группа была видна ТОЛЬКО подсветкой строки
              в колонке слева. Колонки нет ниже 1360 — и на 1024
              человек видел три строки вместо сорока, не понимая
              почему: отбор включён, а сказать об этом некому.
              Крестик снимает отбор оттуда же, где о нём написано.
              ═══════════════════════════════════════════════════════ */}
          {группа !== null && (
            <span data-d="метка-отбора-группы" className={K.меткаОтбораГруппы}>
              <span>группа «{именаГрупп.get(группа) ?? 'без группы'}»</span>
              <button
                type="button"
                title="Показать все кампании"
                aria-label="Показать все кампании"
                className={K.крестикОтбораГруппы}
                onClick={() => {
                  setГруппа(null);
                  setPicked(new Set());
                }}
              >
                <IconClose size={12} штрих={1.8} />
              </button>
            </span>
          )}

          {/* ═══════════════════════════════════════════════════════════
              ПРЕДЕЛЫ ТАРИФА — ЗДЕСЬ, А НЕ В ОТКАЗЕ ПРИ ПОДКЛЮЧЕНИИ.

              Отказ приходит в тот миг, когда решение уже принято,
              и читается как поломка. Число рядом со списком отвечает
              заранее — и отвечает всегда, а не только тогда, когда
              места кончились.
              ═══════════════════════════════════════════════════════ */}
          {/* ТОЛЬКО НА ШИРОКОМ: на 390 та же строка стоит отдельной
              плашкой над таблицей — так в эталоне, и довод целиком
              в `pages/predely.tsx`. */}
          {(местоКабинетов !== null || местоКампаний !== null) && (
            <span data-d="строка-состояния" className={`${K.строкаСостояния} rk-only-wide`}>
              {местоКабинетов !== null && (
                <Предел
                  имя="Кабинеты"
                  узел="предел-кабинетов"
                  занято={местоКабинетов.used}
                  предел={местоКабинетов.limit}
                  подпись={остатокСловами(местоКабинетов.used, местоКабинетов.limit, 'кабинет')}
                />
              )}
              {местоКампаний !== null && (
                <Предел
                  имя="Кампании"
                  узел="предел-кампаний"
                  занято={местоКампаний.used}
                  предел={местоКампаний.limit}
                  подпись={остатокСловами(местоКампаний.used, местоКампаний.limit, 'место')}
                />
              )}
              {/* ═══════════════════════════════════════════════════════
                  ДВА ПРЕДЕЛА ТЕСТОВ, А НЕ ОДИН.

                  Передача 07.09.2026, раздел 3. Тест ставки не занимает
                  место ABC-теста: это разные механики, и мест под них
                  разное число. ABC подменяет главное фото ЖИВОЙ
                  карточки — это виднее покупателю и рискованнее, чем
                  водить ставку по вилке, — поэтому мест под него меньше.

                  ПОРЯДОК НА ШИРОКОМ: Кабинеты · Кампании · Тесты
                  ставок · ABC-тесты. На 390 он другой, довод —
                  у `ПределыПлашкой`.

                  ТАРИФ МОЛЧИТ — ПАРЫ НЕТ. Поле предела заводится
                  в тарифе; пока его там нет, служба отдаёт `null`,
                  и показывать «0 из 0» было бы неправдой.
                  ═══════════════════════════════════════════════════ */}
              {опытыСтавок !== null && (
                <Предел
                  имя="Тесты ставок"
                  узел="предел-тестов-ставок"
                  занято={опытыСтавок.занято}
                  предел={опытыСтавок.предел}
                  подпись={подсказкаПределаСтавок(опытыСтавок.занято, опытыСтавок.предел)}
                />
              )}
              {опытыФото !== null && (
                <Предел
                  имя="ABC-тесты"
                  узел="предел-тестов-фото"
                  занято={опытыФото.занято}
                  предел={опытыФото.предел}
                  подпись={подсказкаПределаФото(опытыФото.занято, опытыФото.предел)}
                  краскаПолоски="var(--serious)"
                />
              )}
              {/* ═══════════════════════════════════════════════════════
                  МЕСТ НЕТ — РЯДОМ ВСТАЁТ ВЫХОД, А НЕ ТОЛЬКО КРАСНОЕ
                  ЧИСЛО.

                  Так в эталоне. Красное «50 из 50» сообщает беду
                  и на этом останавливается; человек идёт искать,
                  где тариф меняют. Кнопка отвечает на «и что теперь»
                  в том же месте, где задан вопрос.
                  ═══════════════════════════════════════════════════ */}
              {местоКампаний !== null && местоКампаний.used >= местоКампаний.limit && (
                <Link
                  data-d="кнопка-поднять-тариф"
                  to="plan"
                  className={`${K.кнопкаПоднятьТариф} no-underline`}
                >
                  Поднять тариф
                </Link>
              )}
            </span>
          )}

          {/* ИТОГО — ПО ПОКАЗАННЫМ СТРОКАМ, А НЕ ПО ВСЕМУ КАБИНЕТУ,
              и это главное о нём. Человек отобрал «не тратят» и хочет
              знать, сколько денег в этой горстке; итог по всему кабинету
              ответил бы на вопрос, которого он не задавал, — и совпал бы
              с плиткой наверху, то есть не сообщил бы ничего. */}
          {/* ═══════════════════════════════════════════════════════
              ОДНОЙ СТРОКОЙ С ТОЧКАМИ, А НЕ ДВУМЯ КУСКАМИ С ЗАЗОРОМ.

              В эталоне текст был длиннее на одно число: «Итого
              за период: 112 542,82 ₽ · заказов 216 · ДРР 13,6 %».
              ДРР отсюда убран сознательно 14.09.2026 — довод целиком
              там, где он раньше считался. Остальное как в эталоне:
              один текст с точками, а не куски с `gap-x-3`, иначе числа
              висят врозь и читаются как разные сообщения.

              ЦИФРЫ ЗДЕСЬ МОНОШИРИННЫЕ, и это тоже из эталона
              (`k-итоги-периода`): строка стоит справа, число под числом,
              и от обновления к обновлению не должна дёргаться. Свойство
              наследуется и на метку цели внутри.
              ═══════════════════════════════════════════════════════ */}
          <span className={K.итогиПериода}>
            {/* ═══════════════════════════════════════════════════════
                ОТ ВСЕЙ СТРОКИ ОСТАЛАСЬ ОДНА МЕТКА ЦЕЛИ.

                Она здесь не ради соседнего числа: ею КРАСИТСЯ ВСЯ ГРАФА
                ДРР в таблице и по ней же считает отбор «Сливают бюджет».
                Убрать метку значило бы спрятать единственное место, где
                предел виден и меняется нажатием.

                СВЕТОФОР ВЕРНУЛСЯ, И КРАСИТ ЕГО ЧИСЛО ПЛИТКИ. 01.09.2026
                владелец просил, чтобы цель подсвечивалась по достижению;
                подсвечивалась она общим ДРР, который 14.09 убрали как
                неверный. Взять вместо него число из плитки — не замена
                похожим, а единственный честный ход: плитка и метка
                теперь выносят ОДИН приговор по ОДНОМУ числу, и разойтись
                им нечем. */}
            <МеткаЦели base={base} кабинет={имяКабинета} достигнуто={дррКабинета} />
          </span>
        </div>

      {/* Полоса появляется только при выделении: постоянная панель
          с кнопками, которые почти всегда ничего не делают, приучает
          их не замечать — а замечать надо ровно тогда, когда опасны. */}
      <BulkBar
        base={base}
        ids={[...picked]}
        total={rows.length}
        /* РАСХОД СЧИТАЕТСЯ ПО ВЫДЕЛЕННЫМ, а не по показанным: полоса
           говорит о цене решения, которое человек принимает прямо
           сейчас, и относиться оно должно ровно к тем строкам,
           на которые уйдёт. */
        расход={shown.filter((i) => picked.has(i.id)).reduce((a, i) => a + i.spend, 0)}
        onClear={() => setPicked(new Set())}
        onDone={setNote}
      />

      {/* ТАБЛИЦА ВМЕСТО СПИСКА КАРТОЧЕК.
          Список отвечал на вопрос «что с этой кампанией» и совсем
          не отвечал на «какая из сорока хуже всех» — а второй вопрос
          и есть работа. Сравнивать числа глазами можно только когда
          они стоят столбиком друг под другом.

          Первый столбец остался прежним: галочка, кнопки, фотографии,
          название, состояние и значки настроек. Менялось то, что справа. */}
        {/* ═══════════════════════════════════════════════════════════
            ВТОРОГО ОКНА ПРОКРУТКИ ЗДЕСЬ БОЛЬШЕ НЕТ.

            01.09.2026 владелец: «2 кампании еле отображаются».
            Разбираясь, нашёл главное: окон прокрутки было ДВА, одно
            в другом, и в обоих стояло одно и то же `100vh - 232px`.

            Внешнее — вот это; внутреннее — `rk-table-scroll` внутри
            самой таблицы. Держало высоту внешнее, а правил я всё это
            время внутреннее: сколько ему ни дай, снаружи стоял тот же
            потолок. Отсюда и пакет 63, в котором «стало меньше»:
            внутреннее ужалось ниже внешнего и стало видно ЕГО.

            ДВА ВЛОЖЕННЫХ СКРОЛЛА — ЭТО ЕЩЁ И НЕВЕРНОЕ ПОВЕДЕНИЕ.
            Колесо, докрутив внутреннее до низа, продолжает внешним:
            строка, которую вели глазами, уезжает. `overscroll-behavior`
            стоял здесь именно от этого — то есть лечил то, что сам же
            и создавал.

            Прокруткой теперь занимается одно окно — то, что внутри
            таблицы: у него липкая шапка столбцов, нижняя боковая
            полоса и `overscroll-behavior: contain` в частице. Здесь
            остаётся обычная обёртка.
            ═══════════════════════════════════════════════════════ */}
        <div>
      {/* ═══════════════════════════════════════════════════════════
          ТАБЛИЦА ИЛИ КАРТОЧКИ — ПО ГРАНИЦЕ 1024, КАК У ВСЕГО КАБИНЕТА.

          Ниже неё четырнадцать колонок не помещаются физически:
          строка уезжает вбок, и уезжает первый столбец — тот, по
          которому строку узнают. Эталон на 390 рисует ДРУГУЮ разметку,
          а не ту же в другом размере, и сборщик частиц честно говорит
          «таблицаКампаний: нет на 390».

          Видимость по-прежнему задают `rk-only-wide`/`rk-only-narrow`.
          VisibleOnce читает тот же медиазапрос синхронно до первого
          показа: скрытая разметка пока не создаётся и не мигает.
          Посещённый вид больше не размонтируется при смене ширины,
          сохраняя открытые формы и порталы. Границу держит
          `ui/breakpoints.spec.ts`.
          ═══════════════════════════════════════════════════════════ */}
      <div className="rk-only-narrow">
        <VisibleOnce hiddenWhen="(min-width: 1024px)">
          <CampaignCards
            rows={rows}
            base={base}
            wsId={params.wsId}
            cabinetId={params.cabinetId}
            picked={picked}
            onPick={toggle}
            onDone={setNote}
            period={period}
            именаГрупп={именаГрупп}
          />
        </VisibleOnce>
      </div>

      <div className="rk-only-wide">
        <VisibleOnce hiddenWhen="(max-width: 1023px)">
          <CampaignTable
            порядок={порядок}
            rows={rows}
            base={base}
            именаГрупп={именаГрупп}
            wsId={params.wsId}
            cabinetId={params.cabinetId}
            picked={picked}
            onPick={toggle}
            onDone={setNote}
            sort={sort}
            desc={desc}
            period={period}
            on={колонки}
            onSort={onSort}
          />
        </VisibleOnce>
      </div>
        </div>

      {/* ═══════════════════════════════════════════════════════════════
          ГРУППА ПУСТА — ПОД ТАБЛИЦЕЙ, А НЕ ВМЕСТО НЕЁ. Так в эталоне:
          шапка со столбцами остаётся (видно, что и в каком порядке
          показывают), а блок стоит ниже. Убери шапку — и экран скажет
          «таблицы нет вовсе», хотя нет всего лишь строк в этой группе.
          ═══════════════════════════════════════════════════════════ */}
      {группаПуста && (
        <Пусто
          вид="группа"
          имя="состояние-группа-пуста"
          знак={<IconBox size={28} />}
          что={<>В группе «{именаГрупп.get(группа as string) ?? 'без группы'}» пока нет кампаний</>}
          почему="Выберите «Все», выделите нужные кампании галочками и нажмите «В группу». Кампания состоит в одной группе."
          кнопка={{ текст: 'Показать все', жать: () => setГруппа(null) }}
        />
      )}
        </>
      )}
        </div>
        </div>
        </div>
      }

      {/* ═══════════════════════════════════════════════════════════════
          ПОДПИСИ ПОД ТАБЛИЦЕЙ ЗДЕСЬ БОЛЬШЕ НЕТ, И ЭТО ПО ЭТАЛОНУ.

          Стояла строка «Деньги — за 7 суток, по отчёту Wildberries.
          Выручку он относит к кампании сам, по своему окну атрибуции».
          В эталоне экран биддера кончается таблицей: ни этой подписи,
          ни какой-либо другой под ней нет.

          СКАЗАННОЕ НЕ ПОТЕРЯНО. Период назван крупно в самом заголовке
          («Кампании за последние 7 дней»), а про то, чей это счёт,
          отвечает подпись плитки «Выручка с рекламы» наверху — там же,
          где стоит само число, а не через экран от него.
          ═══════════════════════════════════════════════════════════ */}

      {holds.isError && (
        <p style={{ color: 'var(--bad-ink)' }}>
          Удержания не прочитались — состояние кампаний показано неполно.
        </p>
      )}
    </div>
  ));
}
